// client/src/hooks/useBookStructureData.ts
// Optimized hook for Book Structure Studio using dedicated endpoints for better performance

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { useToast } from './use-toast';
import { AuthorDetails, FrontMatter, BackMatter } from '@/writer/types';

export interface BookStructureData {
  id: number;
  projectId: number;
  projectUuid: string;
  authorDetails?: AuthorDetails;
  frontMatter?: FrontMatter;
  backMatter?: BackMatter;
  publishingEssentials?: any;
  completionStatus?: {
    authorDetails: boolean;
    frontMatter: number;
    backMatter: number;
    publishingEssentials: boolean;
    overallProgress: number;
  };
  updatedAt: string;
}

export interface BookStructureSummary {
  id: number;
  projectId: number;
  projectUuid: string;
  completionStatus: {
    authorDetails: boolean;
    frontMatter: number;
    backMatter: number;
    publishingEssentials: boolean;
    overallProgress: number;
  };
  updatedAt: string;
}

// Hook for getting complete book structure (for overview/dashboard)
export const useBookStructure = (projectId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['book-structure', projectId],
    queryFn: async (): Promise<BookStructureData> => {
      if (!projectId) throw new Error('Project ID is required');
      
      const response = await apiRequest(`/api/book-structure/${projectId}`, 'GET');
      const data = await response.json();
      
      console.log('📤 [BOOK-STRUCTURE] Complete book structure response:', {
        projectId,
        hasAuthorDetails: !!data.authorDetails && Object.keys(data.authorDetails).length > 0,
        hasFrontMatter: !!data.frontMatter && Object.keys(data.frontMatter).length > 0,
        hasBackMatter: !!data.backMatter && Object.keys(data.backMatter).length > 0,
        responseSize: JSON.stringify(data).length + ' bytes'
      });
      
      return data;
    },
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for getting only author details (optimized for Author Details tab)
export const useAuthorDetails = (projectId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['book-structure-author', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      
      const response = await apiRequest(`/api/book-structure/${projectId}/author-details`, 'GET');
      const data = await response.json();
      
      console.log('📤 [BOOK-STRUCTURE] Author details response:', {
        projectId,
        hasAuthorDetails: !!data.authorDetails && Object.keys(data.authorDetails).length > 0,
        authorName: data.authorDetails?.authorName || 'Not set',
        completionStatus: data.completionStatus,
        responseSize: JSON.stringify(data).length + ' bytes'
      });
      
      return data;
    },
    enabled: enabled && !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for getting front matter
export const useFrontMatter = (projectId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['book-structure-front-matter', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      
      const response = await apiRequest(`/api/book-structure/${projectId}/front-matter`, 'GET');
      const data = await response.json();
      
      console.log('📤 [BOOK-STRUCTURE] Front matter response:', {
        projectId,
        elementCount: data.frontMatter ? Object.keys(data.frontMatter).length : 0,
        responseSize: JSON.stringify(data).length + ' bytes'
      });
      
      return data;
    },
    enabled: enabled && !!projectId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Hook for getting back matter
export const useBackMatter = (projectId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['book-structure-back-matter', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      
      const response = await apiRequest(`/api/book-structure/${projectId}/back-matter`, 'GET');
      const data = await response.json();
      
      console.log('📤 [BOOK-STRUCTURE] Back matter response:', {
        projectId,
        elementCount: data.backMatter ? Object.keys(data.backMatter).length : 0,
        responseSize: JSON.stringify(data).length + ' bytes'
      });
      
      return data;
    },
    enabled: enabled && !!projectId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Hook for getting book structure summary (very lightweight)
export const useBookStructureSummary = (projectId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['book-structure-summary', projectId],
    queryFn: async (): Promise<BookStructureSummary> => {
      if (!projectId) throw new Error('Project ID is required');
      
      const response = await apiRequest(`/api/book-structure/${projectId}/summary`, 'GET');
      const data = await response.json();
      
      console.log('📤 [BOOK-STRUCTURE] Summary response:', {
        projectId,
        completionStatus: data.completionStatus,
        responseSize: JSON.stringify(data).length + ' bytes'
      });
      
      return data;
    },
    enabled: enabled && !!projectId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 3 * 60 * 1000, // 3 minutes
  });
};

// Mutation hook for updating author details
export const useUpdateAuthorDetails = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ projectId, authorDetails }: { projectId: string; authorDetails: AuthorDetails }) => {
      console.log('💾 [BOOK-STRUCTURE] Updating author details:', {
        projectId,
        hasAuthorDetails: !!authorDetails,
        authorName: authorDetails?.authorName || 'Not set'
      });

      const response = await apiRequest(`/api/book-structure/${projectId}/author-details`, 'PUT', {
        authorDetails
      });
      return response.json();
    },
    onSuccess: (data, variables) => {
      console.log('✅ [BOOK-STRUCTURE] Author details updated successfully:', {
        projectId: variables.projectId,
        authorName: data.authorDetails?.authorName || 'Not set'
      });

      // Update specific author details cache
      queryClient.setQueryData(['book-structure-author', variables.projectId], data);
      
      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['book-structure', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['book-structure-summary', variables.projectId] });

      // Also invalidate old project summary cache for backward compatibility
      queryClient.invalidateQueries({ queryKey: ['story-project-summary', variables.projectId] });

      // Fetch updated progress and update cache (following pattern from other save methods)
      (async () => {
        try {
          const progressResponse = await fetch(`/api/story-projects/${variables.projectId}/summary`);
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();

            // Update React Query cache with fresh progress data
            queryClient.setQueryData(['story-project-summary', variables.projectId], progressData);
          } else {
            console.warn('⚠️ Failed to fetch updated progress after author details save:', progressResponse.status);
          }
        } catch (progressError: any) {
          console.error('❌ Progress update error after author details save:', progressError);
        }
      })();

      toast({
        title: "Author Details Saved",
        description: "Your author details have been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      console.error('❌ [BOOK-STRUCTURE] Author details update failed:', error);
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save author details.",
        variant: "destructive",
      });
    },
  });
};

// Mutation hook for updating front matter
export const useUpdateFrontMatter = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      projectId, 
      frontMatter, 
      frontMatterOrder 
    }: { 
      projectId: string; 
      frontMatter: FrontMatter; 
      frontMatterOrder?: string[] 
    }) => {
      console.log('💾 [BOOK-STRUCTURE] Updating front matter:', {
        projectId,
        elementCount: Object.keys(frontMatter).length
      });

      const response = await apiRequest(`/api/book-structure/${projectId}/front-matter`, 'PUT', {
        frontMatter,
        frontMatterOrder
      });
      return response.json();
    },
    onSuccess: (data, variables) => {

      queryClient.setQueryData(['book-structure-front-matter', variables.projectId], data);
      queryClient.invalidateQueries({ queryKey: ['book-structure', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['book-structure-summary', variables.projectId] });
      // CRITICAL FIX: Invalidate project summary cache so BookPreview gets fresh data
      queryClient.invalidateQueries({ queryKey: ['story-project-summary', variables.projectId] });

      // Fetch updated progress and update cache (matching pattern from author details)
      (async () => {
        try {
          const progressResponse = await fetch(`/api/story-projects/${variables.projectId}/summary`);
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();

            // Update React Query cache with fresh progress data
            queryClient.setQueryData(['story-project-summary', variables.projectId], progressData);
          } else {
            console.warn('⚠️ Failed to fetch updated progress after front matter save:', progressResponse.status);
          }
        } catch (progressError: any) {
          console.error('❌ Progress update error after front matter save:', progressError);
        }
      })();

      toast({
        title: "Front Matter Saved",
        description: "Your front matter has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error('❌ [BOOK-STRUCTURE] Front matter update failed:', error);
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save front matter.",
        variant: "destructive",
      });
    },
  });
};

// Mutation hook for updating back matter
export const useUpdateBackMatter = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      projectId, 
      backMatter, 
      backMatterOrder 
    }: { 
      projectId: string; 
      backMatter: BackMatter; 
      backMatterOrder?: string[] 
    }) => {
      console.log('💾 [BOOK-STRUCTURE] Updating back matter:', {
        projectId,
        elementCount: Object.keys(backMatter).length
      });

      const response = await apiRequest(`/api/book-structure/${projectId}/back-matter`, 'PUT', {
        backMatter,
        backMatterOrder
      });
      return response.json();
    },
    onSuccess: (data, variables) => {

      queryClient.setQueryData(['book-structure-back-matter', variables.projectId], data);
      queryClient.invalidateQueries({ queryKey: ['book-structure', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['book-structure-summary', variables.projectId] });
      // CRITICAL FIX: Invalidate project summary cache so BookPreview gets fresh data
      queryClient.invalidateQueries({ queryKey: ['story-project-summary', variables.projectId] });

      // Fetch updated progress and update cache (matching pattern from author details)
      (async () => {
        try {
          const progressResponse = await fetch(`/api/story-projects/${variables.projectId}/summary`);
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();

            // Update React Query cache with fresh progress data
            queryClient.setQueryData(['story-project-summary', variables.projectId], progressData);
          } else {
            console.warn('⚠️ Failed to fetch updated progress after back matter save:', progressResponse.status);
          }
        } catch (progressError: any) {
          console.error('❌ Progress update error after back matter save:', progressError);
        }
      })();

      toast({
        title: "Back Matter Saved",
        description: "Your back matter has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error('❌ [BOOK-STRUCTURE] Back matter update failed:', error);
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save back matter.",
        variant: "destructive",
      });
    },
  });
};

// Export all hooks for easy importing
export const useBookStructureStudio = {
  useBookStructure,
  useAuthorDetails,
  useFrontMatter,
  useBackMatter,
  useBookStructureSummary,
  useUpdateAuthorDetails,
  useUpdateFrontMatter,
  useUpdateBackMatter,
};